<div class="roles-management-page" style="padding: 120px 40px 100px;">

  <!-- Step 3 (permissions) / step 4 (assigning people) of the access chain -->
  <app-policy-flow-steps [active]="activeTab === 'users' ? 'users' : 'permissions'"></app-policy-flow-steps>

  <!-- Header & Top Action Bar -->
  <div class="contests-header mb-4">
    <div>
      <h2 class="page-title">{{ 'roles-page.governance-title' | translate }}</h2>
      <p class="page-subtitle text-muted mb-0">{{ 'roles-page.governance-subtitle' | translate }}</p>
    </div>

    <div class="top-filters">
      <!-- Save Changes Button -->
      <button class="btn btn-primary" (click)="promptSavePermissions()"
        [disabled]="isSavingPermissions || !hasUnsavedChanges || isUsingFallbackPages">
        <span *ngIf="isSavingPermissions" class="spinner-border spinner-border-sm me-2"></span>
        <i *ngIf="!isSavingPermissions" class="fa-solid fa-cloud-arrow-up" style="margin-inline-end: 8px;"></i>
        <span>{{ isSavingPermissions ? ('roles-page.saving' | translate) : ('roles-page.save-permissions' | translate)
          }}</span>
      </button>
    </div>
  </div>

  <!-- Active Role Context Card -->
  <div *ngIf="selectedRole" class="luqta-card active-role-card mb-4">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
      <div class="d-flex align-items-center gap-3">
        <div class="role-icon-box">
          <i class="fa-solid fa-shield-halved"></i>
        </div>
        <div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <h3 class="role-title mb-0">
              {{ getRoleDisplayName(selectedRole) }}
            </h3>
            <span class="badge badge-role-id">
              {{ 'roles-page.id-prefix' | translate }}: #{{ selectedRole.id }}
            </span>
            <span *ngIf="hasUnsavedChanges" class="badge badge-unsaved">
              <span class="pulse-dot"></span> {{ 'roles-page.unsaved-changes' | translate }}
            </span>
          </div>
          <p class="role-desc mb-0 mt-1">
            {{ getRoleDisplayDescription(selectedRole) }}
          </p>
        </div>
      </div>

      <div class="d-flex align-items-center gap-4">
        <div class="text-end">
          <div class="stat-label">{{ 'roles-page.allowed-pages' | translate }}</div>
          <div class="stat-val">
            {{ allowedCount }} / {{ totalPages }}
          </div>
        </div>
        <div class="access-level-box">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <span class="stat-label">{{ 'roles-page.access-level' | translate }}</span>
            <span class="stat-pct">{{ coveragePercent }}%</span>
          </div>
          <div class="progress" style="height: 6px; border-radius: 4px; background: #EAECF0;">
            <div class="progress-bar" role="progressbar" [style.width.%]="coveragePercent"
              style="background: #5304FB; border-radius: 4px;"></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main View Navigation (Page Permissions vs Assign Sub-Users) -->
  <div class="custom-tabs-container mb-4">
    <div class="custom-tabs-header">
      <ul class="custom-nav-tabs" role="tablist">
        <li class="nav-item">
          <a class="nav-link" [class.active]="activeTab === 'permissions'" (click)="switchTab('permissions')"
            role="tab">
            <i class="fa-solid fa-sliders" style="margin-inline-end: 8px;"></i>
            {{ 'roles-page.tab-page-permissions' | translate }}
            <span class="tab-badge" style="margin-inline-start: 8px;"
              [class.tab-badge-active]="activeTab === 'permissions'">
              {{ allowedCount }}/{{ totalPages }}
            </span>
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link" [class.active]="activeTab === 'users'" (click)="switchTab('users')" role="tab">
            <i class="fa-solid fa-users" style="margin-inline-end: 8px;"></i>
            {{ 'roles-page.tab-assign-subusers' | translate }}
            <span class="tab-badge" style="margin-inline-start: 8px;" [class.tab-badge-active]="activeTab === 'users'">
              {{ subUsersList.length }}
            </span>
          </a>
        </li>
      </ul>
    </div>
  </div>

  <!-- TAB 1: Permissions (Page Toggles) -->
  <div *ngIf="activeTab === 'permissions'">
    <!-- Toolbar: Search Bar & Actions -->
    <div class="table-toolbar mb-4">
      <div class="search-bar">
        <div class="icon">
          <i class="fa-solid fa-magnifying-glass"></i>
        </div>
        <input type="text" class="form-control" [(ngModel)]="pageSearchTerm"
          [placeholder]="'roles-page.filter-pages-placeholder' | translate" />
        <i class="fa-solid fa-xmark clear-search" *ngIf="pageSearchTerm" (click)="pageSearchTerm = ''"
          title="Clear search"></i>
      </div>

      <div class="toolbar-actions">
        <button class="btn btn-action btn-action-grant" (click)="grantAll()"
          title="Grant view, create, edit and delete on every page">
          <i class="fa-solid fa-check-double me-2"></i>
          <span>Grant All</span>
        </button>
        <button class="btn btn-action btn-action-revoke" (click)="revokeAll()"
          title="Revoke every permission on every page">
          <i class="fa-solid fa-ban me-2"></i>
          <span>Revoke All</span>
        </button>
        <button *ngIf="hasUnsavedChanges" class="btn btn-action btn-action-revert" (click)="revertChanges()"
          [title]="'roles-page.revert' | translate">
          <i class="fa-solid fa-rotate-left" style="margin-inline-end: 8px;"></i>
          <span>{{ 'roles-page.revert' | translate }}</span>
        </button>
      </div>
    </div>

    <!-- Page list unavailable: ids are placeholders, saving would be rejected -->
    <div *ngIf="isUsingFallbackPages" class="perm-warning mb-3">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <span>
        <strong>Page list unavailable.</strong>
        The server returned no client pages, so the rows below are a local placeholder list and
        <strong>cannot be saved</strong>. Reload once the pages endpoint is available.
      </span>
    </div>

    <!-- CRUD legend -->
    <div class="perm-legend mb-3">
      <i class="fa-solid fa-circle-info"></i>
      <span>
        Each page is controlled by four permissions.
        <strong>View</strong> gates the rest — turning it off automatically revokes Create, Edit and Delete,
        and granting any of those turns View back on.
      </span>
    </div>

    <!-- Permissions Loading State -->
    <div *ngIf="isLoadingPermissions" class="text-center py-5">
      <div class="spinner-border text-primary" role="status" style="color: #5304FB !important;">
        <span class="visually-hidden">{{ 'roles-page.loading-permissions' | translate }}</span>
      </div>
      <div class="mt-2 text-muted small">{{ 'roles-page.loading-pages' | translate }}</div>
    </div>

    <!-- Page Toggles Tree List -->
    <div *ngIf="!isLoadingPermissions" class="luqta-card p-0 overflow-hidden border">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead style="background: #F8F9FA;">
            <tr class="text-muted small">
              <th class="ps-4 py-3" style="width: 28%;">Page Name & Hierarchy</th>
              <th class="py-3" style="width: 17%;">Stable Key (page_key)</th>
              <th class="py-3" style="width: 17%;">Portal Route URL</th>
              <th *ngFor="let action of permissionActions" class="text-center py-3 perm-col-head" style="width: 9.5%;">
                <div class="perm-col-title" nz-tooltip [nzTooltipTitle]="action.hint">
                  <i [class]="action.icon"></i>
                  <span>{{ action.label }}</span>
                </div>
                <div class="perm-col-meta">
                  <span class="perm-col-count">{{ countForAction(action) }}/{{ totalPages }}</span>
                  <button type="button" class="perm-col-btn grant" (click)="toggleColumn(action, true)" nz-tooltip
                    [nzTooltipTitle]="'Grant ' + action.label + ' on every page'">
                    <i class="fa-solid fa-check"></i>
                  </button>
                  <button type="button" class="perm-col-btn revoke" (click)="toggleColumn(action, false)" nz-tooltip
                    [nzTooltipTitle]="'Revoke ' + action.label + ' from every page'">
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let parent of filteredTreePages">
              <!-- Parent Row -->
              <tr [class.table-light]="parent.can_view">
                <td class="ps-4 py-3">
                  <div class="d-flex align-items-center gap-2">
                    <i class="fa-solid fa-folder-open text-primary"
                      style="font-size: 15px; color: #5304FB !important;"></i>
                    <strong style="color: #1E005E; font-size: 14px;">{{ getPageDisplayName(parent) }}</strong>
                    <span *ngIf="parent.children && parent.children.length > 0"
                      class="badge bg-light text-muted border small" style="margin-inline-start: 6px;">
                      {{ parent.children.length }} {{ 'roles-page.sub-pages' | translate }}
                    </span>
                    <button type="button" class="row-all-btn" (click)="toggleRow(parent, !isRowFullyGranted(parent))"
                      nz-tooltip
                      [nzTooltipTitle]="isRowFullyGranted(parent) ? 'Revoke all actions on this page' : 'Grant all actions on this page'">
                      <i class="fa-solid" [class.fa-check-double]="!isRowFullyGranted(parent)"
                        [class.fa-ban]="isRowFullyGranted(parent)"></i>
                    </button>
                  </div>
                </td>
                <td class="py-3">
                  <code class="px-2 py-1 bg-light rounded text-dark font-monospace" style="font-size: 12px;">
                    {{ parent.page_key }}
                  </code>
                </td>
                <td class="py-3 text-muted small font-monospace">
                  {{ parent.url }}
                </td>
                <td *ngFor="let action of permissionActions" class="text-center py-3">
                  <nz-switch [(ngModel)]="parent[action.field]" (ngModelChange)="onToggleAction(parent, action)"
                    [nzDisabled]="isActionDisabled(parent, action)" nzSize="small"></nz-switch>
                </td>
              </tr>

              <!-- Children Rows (Nested Tree) -->
              <tr *ngFor="let child of visibleChildren(parent)" [class.table-light]="child.can_view">
                <td class="ps-5 py-2">
                  <div class="d-flex align-items-center gap-2 ps-3"
                    style="border-inline-start: 2px solid #D0D5DD; margin-inline-start: 12px;">
                    <i class="fa-solid fa-turn-up fa-rotate-90 text-muted" style="font-size: 11px;"></i>
                    <span style="color: #344054; font-size: 13.5px;">{{ child.page_name }}</span>
                    <button type="button" class="row-all-btn" (click)="toggleRow(child, !isRowFullyGranted(child))"
                      nz-tooltip
                      [nzTooltipTitle]="isRowFullyGranted(child) ? 'Revoke all actions on this page' : 'Grant all actions on this page'">
                      <i class="fa-solid" [class.fa-check-double]="!isRowFullyGranted(child)"
                        [class.fa-ban]="isRowFullyGranted(child)"></i>
                    </button>
                  </div>
                </td>
                <td class="py-2">
                  <code class="px-2 py-1 bg-light rounded text-muted font-monospace" style="font-size: 11.5px;">
                    {{ child.page_key }}
                  </code>
                </td>
                <td class="py-2 text-muted small font-monospace">
                  {{ child.url }}
                </td>
                <td *ngFor="let action of permissionActions" class="text-center py-2">
                  <nz-switch [(ngModel)]="child[action.field]" (ngModelChange)="onToggleAction(child, action)"
                    [nzDisabled]="isActionDisabled(child, action)" nzSize="small"></nz-switch>
                </td>
              </tr>
            </ng-container>

            <tr *ngIf="filteredTreePages.length === 0">
              <td [attr.colspan]="3 + permissionActions.length" class="text-center py-5 text-muted">
                <i class="fa-solid fa-filter text-muted mb-2" style="font-size: 28px;"></i>
                <div>{{ 'roles-page.no-pages-match' | translate:{ term: pageSearchTerm } }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- TAB 2: Assign Sub-Users -->
  <div *ngIf="activeTab === 'users'">
    <!-- Toolbar: Search Bar & Info -->
    <div class="table-toolbar mb-4">
      <div class="search-bar">
        <div class="icon">
          <i class="fa-solid fa-magnifying-glass"></i>
        </div>
        <input type="text" class="form-control" [(ngModel)]="userSearchTerm"
          [placeholder]="'roles-page.search-subusers-placeholder' | translate" />
        <i class="fa-solid fa-xmark clear-search" *ngIf="userSearchTerm" (click)="userSearchTerm = ''"
          title="Clear search"></i>
      </div>

      <div class="toolbar-hint text-muted small">
        <i class="fa-solid fa-circle-info" style="color: #5304FB; margin-inline-end: 6px;"></i>
        {{ 'roles-page.assign-hint' | translate }}
      </div>
    </div>

    <!-- Users Loading State -->
    <div *ngIf="isLoadingUsers" class="text-center py-5">
      <div class="spinner-border text-primary" role="status" style="color: #5304FB !important;">
        <span class="visually-hidden">{{ 'roles-page.loading-subusers' | translate }}</span>
      </div>
      <div class="mt-2 text-muted small">{{ 'roles-page.fetching-subusers' | translate }}</div>
    </div>

    <!-- Sub-Users Assignment Table -->
    <div *ngIf="!isLoadingUsers" class="luqta-card p-0 overflow-hidden border">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead style="background: #F8F9FA;">
            <tr class="text-muted small">
              <th class="ps-4 py-3" style="width: 40%;">{{ 'roles-page.col-subuser' | translate }}</th>
              <th class="py-3" style="width: 30%;">{{ 'roles-page.col-email' | translate }}</th>
              <th class="py-3" style="width: 30%;">{{ 'roles-page.col-assigned-role' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let user of filteredSubUsers">
              <td class="ps-4 py-3">
                <div class="d-flex align-items-center gap-3">
                  <div
                    class="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold"
                    style="width: 38px; height: 38px; background: #5304FB; font-size: 13px;">
                    {{ user.name.charAt(0).toUpperCase() }}
                  </div>
                  <div>
                    <strong style="color: #1E005E; font-size: 14px;">{{ user.name }}</strong>
                    <div class="text-muted small">{{ 'roles-page.id-prefix' | translate }}: #{{ user.id }}</div>
                  </div>
                </div>
              </td>
              <td class="py-3 text-muted small">
                {{ user.email }}
              </td>
              <td class="py-3">
                <div class="d-flex align-items-center gap-2 role-select-wrapper" style="max-width: 290px;">
                  <nz-select class="role-select-dropdown w-100" [ngModel]="user.role_id"
                    (ngModelChange)="onSubUserRoleChange(user, $event)" [nzDisabled]="!!user.isUpdating"
                    [nzCustomTemplate]="roleDisplayTemplate" nzDropdownClassName="role-dropdown-popup"
                    [nzOptionHeightPx]="50" [nzOptionOverflowSize]="8"
                    [nzPlaceHolder]="'roles-page.select-policy-role' | translate">
                    <nz-option [nzValue]="0" [nzLabel]="'roles-page.no-role-denied' | translate" nzCustomContent>
                      <div class="d-flex align-items-center gap-2 py-1">
                        <span class="role-status-dot role-status-denied"></span>
                        <span class="role-option-text role-option-denied">{{ 'roles-page.no-role-denied' | translate
                          }}</span>
                      </div>
                    </nz-option>
                    <nz-option *ngFor="let r of rolesList" [nzValue]="r.id" [nzLabel]="getRoleDisplayName(r)"
                      nzCustomContent>
                      <div class="d-flex align-items-center justify-content-between gap-2 w-100 py-1">
                        <div class="d-flex align-items-center gap-2">
                          <span class="role-status-dot role-status-active"></span>
                          <span class="role-option-text font-weight-500">{{ getRoleDisplayName(r) }}</span>
                        </div>
                        <span class="badge badge-custom"
                          style="font-size: 11px; padding: 2px 7px; background: #EAE4FF; color: #5304FB; border-radius: 12px;">
                          #{{ r.id }}
                        </span>
                      </div>
                    </nz-option>
                  </nz-select>

                  <ng-template #roleDisplayTemplate let-selected>
                    <div class="d-flex align-items-center gap-2 text-truncate" *ngIf="selected.nzValue === 0">
                      <span class="role-status-dot role-status-denied"></span>
                      <span style="color: #667085; font-size: 13px; font-weight: 500;">{{ 'roles-page.no-role-denied' |
                        translate }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-2 text-truncate" *ngIf="selected.nzValue !== 0">
                      <span class="role-status-dot role-status-active"></span>
                      <span style="color: #1E005E; font-weight: 600; font-size: 13px;">{{ selected.nzLabel }}</span>
                      <span class="badge badge-custom ms-1"
                        style="font-size: 10px; padding: 1px 6px; background: #EAE4FF; color: #5304FB; border-radius: 8px;">
                        #{{ selected.nzValue }}
                      </span>
                    </div>
                  </ng-template>

                  <span *ngIf="user.isUpdating" class="spinner-border spinner-border-sm text-primary flex-shrink-0"
                    role="status" style="color: #5304FB !important;"></span>
                </div>
              </td>
            </tr>

            <tr *ngIf="filteredSubUsers.length === 0">
              <td colspan="3" class="text-center py-5 text-muted">
                <i class="fa-solid fa-users-slash text-muted mb-2" style="font-size: 32px;"></i>
                <div>{{ 'roles-page.no-subusers-found' | translate }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>


</div>